// One-line summary of what a grouped set of queued-edit changes does, for the
// group header of a collapsed section. Members are hidden by default there, so
// the header has to carry the "what does approving this do" answer on its own.
//
// Proposer-supplied intents are the signal when present; entries without one
// fall back to a counted verb + resource phrase so a group is never summarized
// as nothing.

import pluralize from 'pluralize'
import type { ChangeVerb, ResourceChangeSet } from '#src/queued-edits/model.ts'

const VERB_LABEL: Record<ChangeVerb, string> = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
}

const SEPARATOR = ' · '

// `ServiceRequest` → `service request`, `CarePlan` → `care plan`. Acronym runs
// stay together (`HbA1c` does not explode into single letters).
function humanizeResourceType(resourceType: string): string {
  return resourceType
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase()
}

function withCount(phrase: string, count: number): string {
  return count > 1 ? `${phrase} ×${count}` : phrase
}

// Summarize a group's member changes as a single line, e.g.
// `Void the care plan · Void the goal ×2 · Update 2 tasks`. Intents dedupe
// case-insensitively in first-seen order; entries with no intent collapse into
// one counted phrase per verb + resource type. A single distinct phrase drops
// its count. Returns '' for an empty list.
export function summarizeGroupIntents(changes: ResourceChangeSet[]): string {
  const intents = new Map<string, { phrase: string; count: number }>()
  const fallbacks = new Map<
    string,
    { verb: ChangeVerb; resourceType: string; count: number }
  >()

  for (const change of changes) {
    const intent = change.intent?.trim()
    if (intent) {
      const key = intent.toLowerCase()
      const existing = intents.get(key)
      if (existing) {
        existing.count += 1
      } else {
        intents.set(key, { phrase: intent, count: 1 })
      }
      continue
    }
    const key = `${change.verb}:${change.resourceType}`
    const existing = fallbacks.get(key)
    if (existing) {
      existing.count += 1
    } else {
      fallbacks.set(key, {
        verb: change.verb,
        resourceType: change.resourceType,
        count: 1,
      })
    }
  }

  // A lone intent is the whole story; its count would only echo the member
  // count the header already shows beside the label.
  const countIntents = intents.size + fallbacks.size > 1
  const parts = [
    ...[...intents.values()].map(({ phrase, count }) =>
      countIntents ? withCount(phrase, count) : phrase
    ),
    ...[...fallbacks.values()].map(({ verb, resourceType, count }) => {
      const noun = humanizeResourceType(resourceType)
      return `${VERB_LABEL[verb]} ${count} ${pluralize(noun, count)}`
    }),
  ]

  return parts.join(SEPARATOR)
}
